/**
 * PixelBlock Python Blocks definition for ScratchBlocks (Blockly).
 */
import PIXELBLOCK_COLORS from './colors.js';
import {
    getPixelBlockLocale,
    getPixelBlockMsg,
    getArithmeticOptions,
    getCompareOptions,
    getBoolOpOptions,
    getBooleanOptions,
    getVarChangeOptions
} from './messages.js';

export function registerPixelBlockBlocks(ScratchBlocks) {
    if (!ScratchBlocks || !ScratchBlocks.Blocks) return;

    const applyColors = (block, category) => {
        const c = PIXELBLOCK_COLORS[category];
        if (c && block.setColour) {
            block.setColour(c.colourPrimary, c.colourSecondary, c.colourTertiary);
        }
    };

    function fireMutationChangeEvent(block, oldMutationDom) {
        if (!ScratchBlocks || !ScratchBlocks.Events || !ScratchBlocks.Events.isEnabled()) return;
        try {
            const oldMutation = oldMutationDom ? ScratchBlocks.Xml.domToText(oldMutationDom) : "";
            const newMutationDom = block.mutationToDom ? block.mutationToDom() : null;
            const newMutation = newMutationDom ? ScratchBlocks.Xml.domToText(newMutationDom) : "";
            if (oldMutation !== newMutation) {
                const eventClass = ScratchBlocks.Events.get(ScratchBlocks.Events.BLOCK_CHANGE);
                if (eventClass) {
                    ScratchBlocks.Events.fire(
                        new eventClass(block, "mutation", null, oldMutation, newMutation)
                    );
                }
            }
        } catch (e) {
            console.warn("Failed to fire mutation event:", e);
        }
    }

    // Hook Gesture to allow dragging loop variable directly from for loop blocks
    if (ScratchBlocks && ScratchBlocks.Gesture && !ScratchBlocks.Gesture.__pixelBlockPatched) {
        ScratchBlocks.Gesture.__pixelBlockPatched = true;
        const origUpdateIsDragging = ScratchBlocks.Gesture.prototype.updateIsDragging;
        ScratchBlocks.Gesture.prototype.updateIsDragging = function (e) {
            if (this.startField && !this.calledUpdateIsDragging) {
                const sourceBlock = typeof this.startField.getSourceBlock === "function"
                    ? this.startField.getSourceBlock()
                    : null;
                if (sourceBlock && !sourceBlock.isInFlyout) {
                    const isForLoop = sourceBlock.type === "python_for_range" ||
                        sourceBlock.type === "python_for_range_step" ||
                        sourceBlock.type === "python_for_in";
                    const isLoopVar = (this.startField.name === "VAR" || this.startField.isLoopVar) && isForLoop;

                    if (isLoopVar && this.startWorkspace_) {
                        let varName = (this.startField.getText ? this.startField.getText() : null) ||
                            (this.startField.getValue ? this.startField.getValue() : null) ||
                            "i";
                        varName = String(varName).trim();
                        if (!varName) varName = sourceBlock.type === "python_for_in" ? "item" : "i";

                        const ws = this.startWorkspace_;
                        try {
                            let variable = ws.getVariable ? ws.getVariable(varName, "") : null;
                            if (!variable && ws.createVariable) {
                                variable = ws.createVariable(varName, "");
                            }
                            const newBlock = ws.newBlock("python_variable_get");
                            const vField = newBlock.getField("VAR");
                            if (vField) {
                                if (variable && typeof vField.setValue === "function") {
                                    vField.setValue(variable.getId());
                                } else {
                                    newBlock.setFieldValue(varName, "VAR");
                                }
                            }
                            newBlock.initSvg();
                            newBlock.render();

                            let targetX = 0;
                            let targetY = 0;
                            if (e && typeof e.clientX === "number" && ScratchBlocks.utils && ScratchBlocks.utils.svgMath && ScratchBlocks.utils.svgMath.screenToWsCoordinates) {
                                const wsCoord = ScratchBlocks.utils.svgMath.screenToWsCoordinates(
                                    ws,
                                    new ScratchBlocks.utils.Coordinate(e.clientX, e.clientY)
                                );
                                targetX = wsCoord.x - 25;
                                targetY = wsCoord.y - 15;
                            } else {
                                const sourceXY = sourceBlock.getRelativeToSurfaceXY();
                                targetX = sourceXY.x + 30;
                                targetY = sourceXY.y;
                            }
                            newBlock.moveTo(new ScratchBlocks.utils.Coordinate(targetX, targetY));

                            if (this.targetBlock && typeof this.targetBlock.unselect === "function") {
                                this.targetBlock.unselect();
                            }
                            this.startBlock = newBlock;
                            this.targetBlock = newBlock;
                            if (ScratchBlocks.common && ScratchBlocks.common.setSelected) {
                                ScratchBlocks.common.setSelected(newBlock);
                            } else if (typeof newBlock.select === "function") {
                                newBlock.select();
                            }
                        } catch (err) {
                            console.error("Error duplicating loop variable on drag:", err);
                        }
                    }
                }
            }
            return origUpdateIsDragging.call(this, e);
        };
    }


    // ==========================================
    // 1. VARIABLES
    // ==========================================
    ScratchBlocks.Blocks.python_variable_set = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('VAR_SET', 'set %1 = %2'),
                args0: [
                    { type: 'field_variable', name: 'VAR', variable: 'x', variableTypes: [''], defaultType: '' },
                    { type: 'input_value', name: 'VALUE' }
                ],
                category: 'variables',
                extensions: ['shape_statement']
            });
            applyColors(this, 'variables');
        }
    };

    ScratchBlocks.Blocks.python_variable_change = {
        init: function () {
            const loc = getPixelBlockLocale();
            this.jsonInit({
                message0: getPixelBlockMsg('VAR_CHANGE', '%1 %2 by %3'),
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'OP',
                        options: getVarChangeOptions(loc)
                    },
                    { type: 'field_variable', name: 'VAR', variable: 'x', variableTypes: [''], defaultType: '' },
                    { type: 'input_value', name: 'VALUE' }
                ],
                category: 'variables',
                extensions: ['shape_statement']
            });
            applyColors(this, 'variables');
        }
    };

    ScratchBlocks.Blocks.python_variable_get = {
        init: function () {
            this.jsonInit({
                message0: '%1',
                args0: [
                    { type: 'field_variable', name: 'VAR', variable: 'x', variableTypes: [''], defaultType: '' }
                ],
                category: 'variables',
                extensions: ['output_string']
            });
            applyColors(this, 'variables');
        }
    };

    // ==========================================
    // 2. DECISIONS / CONDITION
    // ==========================================

    const PLUS_SVG_DATA = "data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"22\" height=\"22\" viewBox=\"0 0 22 22\"><circle cx=\"11\" cy=\"11\" r=\"9.5\" fill=\"%23259fc5\" stroke=\"%23ffffff\" stroke-width=\"1.5\"/><line x1=\"11\" y1=\"6.5\" x2=\"11\" y2=\"15.5\" stroke=\"%23ffffff\" stroke-width=\"2\" stroke-linecap=\"round\"/><line x1=\"6.5\" y1=\"11\" x2=\"15.5\" y2=\"11\" stroke=\"%23ffffff\" stroke-width=\"2\" stroke-linecap=\"round\"/></svg>";
    const MINUS_SVG_DATA = "data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"22\" height=\"22\" viewBox=\"0 0 22 22\"><circle cx=\"11\" cy=\"11\" r=\"9.5\" fill=\"%23e25353\" stroke=\"%23ffffff\" stroke-width=\"1.5\"/><line x1=\"6.5\" y1=\"11\" x2=\"15.5\" y2=\"11\" stroke=\"%23ffffff\" stroke-width=\"2\" stroke-linecap=\"round\"/></svg>";

    ScratchBlocks.Blocks.python_if = {
        init: function () {
            this.elifCount_ = 0;
            this.hasElse_ = false;

            this.jsonInit({
                message0: getPixelBlockMsg('IF', 'if %1 :'),
                message1: '%1',
                args0: [
                    { type: 'input_value', name: 'CONDITION' }
                ],
                args1: [
                    { type: 'input_statement', name: 'SUBSTACK' }
                ],
                category: 'control',
                extensions: ['shape_statement']
            });
            applyColors(this, 'control');
            this.updateShape_();
        },

        mutationToDom: function () {
            const container = document.createElement('mutation');
            container.setAttribute('else', this.hasElse_ ? '1' : '0');
            container.setAttribute('elif', String(this.elifCount_ || 0));
            return container;
        },

        domToMutation: function (xmlElement) {
            this.hasElse_ = xmlElement.getAttribute('else') === '1' || (!xmlElement.hasAttribute('else') && this.type === 'python_ifelse');
            this.elifCount_ = parseInt(xmlElement.getAttribute('elif'), 10) || 0;
            this.rebuildShape_();
        },

        saveExtraState: function () {
            return {
                hasElse: Boolean(this.hasElse_),
                elifCount: this.elifCount_ || 0
            };
        },

        loadExtraState: function (state) {
            if (state) {
                this.hasElse_ = state.hasElse !== undefined ? Boolean(state.hasElse) : (this.type === 'python_ifelse');
                this.elifCount_ = Number(state.elifCount) || 0;
                this.rebuildShape_();
            }
        },

        setElifAndElse: function (elifCount, hasElse) {
            const oldDom = this.mutationToDom();
            this.elifCount_ = Math.max(0, elifCount || 0);
            this.hasElse_ = Boolean(hasElse);
            this.rebuildShape_();
            fireMutationChangeEvent(this, oldDom);
        },

        customContextMenu: function (options) {
            if (this.isInFlyout) return;
            if (!this.hasElse_) {
                options.push({
                    enabled: true,
                    text: getPixelBlockMsg('CTX_ADD_ELSE', 'Add else'),
                    callback: () => {
                        const oldDom = this.mutationToDom();
                        this.hasElse_ = true;
                        this.rebuildShape_();
                        fireMutationChangeEvent(this, oldDom);
                    }
                });
            }
            options.push({
                enabled: true,
                text: getPixelBlockMsg('CTX_ADD_ELIF', 'Add elif'),
                callback: () => {
                    const oldDom = this.mutationToDom();
                    this.hasElse_ = true;
                    this.elifCount_++;
                    this.rebuildShape_();
                    fireMutationChangeEvent(this, oldDom);
                }
            });
            if (this.hasElse_ || this.elifCount_ > 0) {
                options.push({
                    enabled: true,
                    text: getPixelBlockMsg('CTX_REMOVE_BRANCH', 'Remove last branch'),
                    callback: () => {
                        const oldDom = this.mutationToDom();
                        if (this.elifCount_ > 0) {
                            this.elifCount_--;
                        } else if (this.hasElse_) {
                            this.hasElse_ = false;
                        }
                        this.rebuildShape_();
                        fireMutationChangeEvent(this, oldDom);
                    }
                });
            }
        },

        rebuildShape_: function () {
            const elifValueConns = [null];
            const elifStmtConns = [null];
            let elseStmtConn = null;

            for (let i = 1; this.getInput('CONDITION' + i); i++) {
                const condInput = this.getInput('CONDITION' + i);
                const stmtInput = this.getInput('SUBSTACK' + i);
                elifValueConns.push(condInput && condInput.connection ? condInput.connection.targetConnection : null);
                elifStmtConns.push(stmtInput && stmtInput.connection ? stmtInput.connection.targetConnection : null);
            }

            if (this.getInput('SUBSTACK_ELSE')) {
                const elseInput = this.getInput('SUBSTACK_ELSE');
                elseStmtConn = elseInput && elseInput.connection ? elseInput.connection.targetConnection : null;
            }

            this.updateShape_();

            for (let i = 1; i <= this.elifCount_; i++) {
                if (elifValueConns[i]) {
                    const input = this.getInput('CONDITION' + i);
                    if (input && input.connection) {
                        try { elifValueConns[i].reconnect(this, 'CONDITION' + i); } catch (e) {}
                    }
                }
                if (elifStmtConns[i]) {
                    const input = this.getInput('SUBSTACK' + i);
                    if (input && input.connection) {
                        try { elifStmtConns[i].reconnect(this, 'SUBSTACK' + i); } catch (e) {}
                    }
                }
            }

            if (elseStmtConn && this.hasElse_) {
                const input = this.getInput('SUBSTACK_ELSE');
                if (input && input.connection) {
                    try { elseStmtConn.reconnect(this, 'SUBSTACK_ELSE'); } catch (e) {}
                }
            }

            if (this.rendered) {
                this.render();
            }
        },

        updateShape_: function () {
            if (this.getInput('BUTTONS')) {
                this.removeInput('BUTTONS');
            }

            if (this.getInput('ELSE_LABEL')) {
                this.removeInput('ELSE_LABEL');
            }
            if (this.getInput('SUBSTACK_ELSE')) {
                this.removeInput('SUBSTACK_ELSE');
            }

            for (let i = 1; i <= 100; i++) {
                let found = false;
                if (this.getInput('CONDITION' + i)) {
                    this.removeInput('CONDITION' + i);
                    found = true;
                }
                if (this.getInput('THEN' + i)) {
                    this.removeInput('THEN' + i);
                    found = true;
                }
                if (this.getInput('SUBSTACK' + i)) {
                    this.removeInput('SUBSTACK' + i);
                    found = true;
                }
                if (!found && i > (this.elifCount_ || 0)) break;
            }

            for (let i = 1; i <= this.elifCount_; i++) {
                const condInput = this.appendValueInput('CONDITION' + i);
                condInput.appendField(getPixelBlockMsg('ELIF_LABEL', 'elif'));
                const thenInput = this.appendDummyInput('THEN' + i);
                thenInput.appendField(getPixelBlockMsg('THEN_LABEL', ':'));
                this.appendStatementInput('SUBSTACK' + i);
            }

            if (this.hasElse_) {
                const elseInput = this.appendDummyInput('ELSE_LABEL');
                elseInput.appendField(getPixelBlockMsg('ELSE_LABEL', 'else :'));
                this.appendStatementInput('SUBSTACK_ELSE');
            }

            const buttonsInput = this.appendDummyInput('BUTTONS');
            const plusBtn = new ScratchBlocks.FieldImage(
                PLUS_SVG_DATA,
                22,
                22,
                '+',
                () => {
                    if (this.isInFlyout) return;
                    const oldDom = this.mutationToDom();
                    if (!this.hasElse_) {
                        this.hasElse_ = true;
                    } else {
                        this.elifCount_++;
                    }
                    this.rebuildShape_();
                    fireMutationChangeEvent(this, oldDom);
                }
            );
            buttonsInput.appendField(plusBtn, 'PLUS_BTN');

            if (this.hasElse_ || this.elifCount_ > 0) {
                buttonsInput.appendField(new ScratchBlocks.FieldLabel(' '), 'SEP');
                const minusBtn = new ScratchBlocks.FieldImage(
                    MINUS_SVG_DATA,
                    22,
                    22,
                    '-',
                    () => {
                        if (this.isInFlyout) return;
                        const oldDom = this.mutationToDom();
                        if (this.elifCount_ > 0) {
                            this.elifCount_--;
                        } else if (this.hasElse_) {
                            this.hasElse_ = false;
                        }
                        this.rebuildShape_();
                        fireMutationChangeEvent(this, oldDom);
                    }
                );
                buttonsInput.appendField(minusBtn, 'MINUS_BTN');
            }

            applyColors(this, 'control');
        }
    };

    ScratchBlocks.Blocks.python_ifelse = {
        init: function () {
            this.elifCount_ = 0;
            this.hasElse_ = true;

            this.jsonInit({
                message0: getPixelBlockMsg('IF', 'if %1 :'),
                message1: '%1',
                args0: [
                    { type: 'input_value', name: 'CONDITION' }
                ],
                args1: [
                    { type: 'input_statement', name: 'SUBSTACK' }
                ],
                category: 'control',
                extensions: ['shape_statement']
            });
            applyColors(this, 'control');
            this.updateShape_();
        },

        mutationToDom: ScratchBlocks.Blocks.python_if.mutationToDom,
        domToMutation: ScratchBlocks.Blocks.python_if.domToMutation,
        saveExtraState: ScratchBlocks.Blocks.python_if.saveExtraState,
        loadExtraState: ScratchBlocks.Blocks.python_if.loadExtraState,
        setElifAndElse: ScratchBlocks.Blocks.python_if.setElifAndElse,
        customContextMenu: ScratchBlocks.Blocks.python_if.customContextMenu,
        rebuildShape_: ScratchBlocks.Blocks.python_if.rebuildShape_,
        updateShape_: ScratchBlocks.Blocks.python_if.updateShape_
    };

    ScratchBlocks.Blocks.python_if_elif = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('IF', 'if %1 :'),
                message1: '%1',
                message2: 'elif %1 :',
                message3: '%1',
                args0: [
                    { type: 'input_value', name: 'CONDITION1' }
                ],
                args1: [
                    { type: 'input_statement', name: 'SUBSTACK1' }
                ],
                args2: [
                    { type: 'input_value', name: 'CONDITION2' }
                ],
                args3: [
                    { type: 'input_statement', name: 'SUBSTACK2' }
                ],
                category: 'control',
                extensions: ['shape_statement']
            });
            applyColors(this, 'control');
        }
    };

    ScratchBlocks.Blocks.python_if_elif_else = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('IF', 'if %1 :'),
                message1: '%1',
                message2: 'elif %1 :',
                message3: '%1',
                message4: 'else :',
                message5: '%1',
                args0: [
                    { type: 'input_value', name: 'CONDITION1' }
                ],
                args1: [
                    { type: 'input_statement', name: 'SUBSTACK1' }
                ],
                args2: [
                    { type: 'input_value', name: 'CONDITION2' }
                ],
                args3: [
                    { type: 'input_statement', name: 'SUBSTACK2' }
                ],
                args5: [
                    { type: 'input_statement', name: 'SUBSTACK3' }
                ],
                category: 'control',
                extensions: ['shape_statement']
            });
            applyColors(this, 'control');
        }
    };

    ScratchBlocks.Blocks.python_if_elif_elif_else = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('IF', 'if %1 :'),
                message1: '%1',
                message2: 'elif %1 :',
                message3: '%1',
                message4: 'elif %1 :',
                message5: '%1',
                message6: 'else :',
                message7: '%1',
                args0: [
                    { type: 'input_value', name: 'CONDITION1' }
                ],
                args1: [
                    { type: 'input_statement', name: 'SUBSTACK1' }
                ],
                args2: [
                    { type: 'input_value', name: 'CONDITION2' }
                ],
                args3: [
                    { type: 'input_statement', name: 'SUBSTACK2' }
                ],
                args4: [
                    { type: 'input_value', name: 'CONDITION3' }
                ],
                args5: [
                    { type: 'input_statement', name: 'SUBSTACK3' }
                ],
                args7: [
                    { type: 'input_statement', name: 'SUBSTACK4' }
                ],
                category: 'control',
                extensions: ['shape_statement']
            });
            applyColors(this, 'control');
        }
    };

    ScratchBlocks.Blocks.python_compare = {
        init: function () {
            this.jsonInit({
                message0: '%1 %2 %3',
                args0: [
                    { type: 'input_value', name: 'A' },
                    {
                        type: 'field_dropdown',
                        name: 'OP',
                        options: getCompareOptions()
                    },
                    { type: 'input_value', name: 'B' }
                ],
                category: 'operators',
                extensions: ['output_boolean']
            });
            applyColors(this, 'operators');
        }
    };

    ScratchBlocks.Blocks.python_bool_op = {
        init: function () {
            this.jsonInit({
                message0: '%1 %2 %3',
                args0: [
                    { type: 'input_value', name: 'A' },
                    {
                        type: 'field_dropdown',
                        name: 'OP',
                        options: getBoolOpOptions()
                    },
                    { type: 'input_value', name: 'B' }
                ],
                category: 'operators',
                extensions: ['output_boolean']
            });
            applyColors(this, 'operators');
        }
    };

    ScratchBlocks.Blocks.python_not = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('NOT', 'not %1'),
                args0: [
                    { type: 'input_value', name: 'A' }
                ],
                category: 'operators',
                extensions: ['output_boolean']
            });
            applyColors(this, 'operators');
        }
    };

    // ==========================================
    // 3. ITERATION / BOUCLES
    // ==========================================
        function setupLoopVarField (block) {
        const varField = block.getField("VAR");
        if (varField) {
            varField.isLoopVar = true;
            if (typeof varField.setValidator === "function") {
                varField.setValidator(function (newText) {
                    if (!newText) return null;
                    const trimmed = newText.trim();
                    if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(trimmed)) {
                        return trimmed;
                    }
                    return null;
                });
            }
            varField.onFinishEditing_ = function (finalVal) {
                if (!finalVal) return;
                const trimmed = String(finalVal).trim();
                if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(trimmed)) {
                    const ws = block.workspace;
                    if (ws && ws.createVariable && !ws.getVariable(trimmed, "")) {
                        ws.createVariable(trimmed, "");
                    }
                }
            };
            const applyClass = () => {
                if (varField.fieldGroup_) {
                    varField.fieldGroup_.classList.add("pixelblock-loop-var-field");
                }
            };
            if (varField.fieldGroup_) {
                applyClass();
            } else {
                const origInitView = varField.initView;
                varField.initView = function () {
                    if (origInitView) origInitView.call(this);
                    applyClass();
                };
            }
        }
    }

    function addForLoopContextMenu (block) {
        block.customContextMenu = function (options) {
            if (this.isInFlyout) return;
            const varName = this.getFieldValue("VAR") || (this.type === "python_for_in" ? "item" : "i");
            const isFr = ScratchBlocks.ScratchMsgs && ScratchBlocks.ScratchMsgs.currentLocale === "fr";
            options.push({
                enabled: true,
                text: isFr ? `Créer « ${varName} »` : `Create ${varName}`,
                callback: () => {
                    const ws = this.workspace;
                    if (!ws) return;
                    let variable = ws.getVariable ? ws.getVariable(varName, "") : null;
                    if (!variable && ws.createVariable) {
                        variable = ws.createVariable(varName, "");
                    }
                    const newBlock = ws.newBlock("python_variable_get");
                    const vField = newBlock.getField("VAR");
                    if (vField) {
                        if (variable && typeof vField.setValue === "function") {
                            vField.setValue(variable.getId());
                        } else {
                            newBlock.setFieldValue(varName, "VAR");
                        }
                    }
                    newBlock.initSvg();
                    newBlock.render();
                    const xy = this.getRelativeToSurfaceXY();
                    newBlock.moveBy(xy.x + 40, xy.y + 40);
                }
            });
        };
    }

    ScratchBlocks.Blocks.python_for_range = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('FOR_RANGE', 'for %1 in range ( %2 , %3 ) :'),
                message1: '%1',
                args0: [
                    { type: 'field_input', name: 'VAR', text: 'i' },
                    { type: 'input_value', name: 'START' },
                    { type: 'input_value', name: 'STOP' }
                ],
                args1: [
                    { type: 'input_statement', name: 'SUBSTACK' }
                ],
                category: 'control',
                extensions: ['shape_statement']
            });
            applyColors(this, 'control');
            setupLoopVarField(this);
            addForLoopContextMenu(this);
        }
    };

    ScratchBlocks.Blocks.python_for_range_step = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('FOR_RANGE_STEP', 'for %1 in range ( %2 , %3 , %4 ) :'),
                message1: '%1',
                args0: [
                    { type: 'field_input', name: 'VAR', text: 'i' },
                    { type: 'input_value', name: 'START' },
                    { type: 'input_value', name: 'STOP' },
                    { type: 'input_value', name: 'STEP' }
                ],
                args1: [
                    { type: 'input_statement', name: 'SUBSTACK' }
                ],
                category: 'control',
                extensions: ['shape_statement']
            });
            applyColors(this, 'control');
            setupLoopVarField(this);
            addForLoopContextMenu(this);
        }
    };

    ScratchBlocks.Blocks.python_for_in = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('FOR_IN', 'for %1 in %2 :'),
                message1: '%1',
                args0: [
                    { type: 'field_input', name: 'VAR', text: 'item' },
                    { type: 'input_value', name: 'LIST' }
                ],
                args1: [
                    { type: 'input_statement', name: 'SUBSTACK' }
                ],
                category: 'control',
                extensions: ['shape_statement']
            });
            applyColors(this, 'control');
            setupLoopVarField(this);
            addForLoopContextMenu(this);
        }
    };

    ScratchBlocks.Blocks.python_while = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('WHILE', 'while %1 :'),
                message1: '%1',
                args0: [
                    { type: 'input_value', name: 'CONDITION' }
                ],
                args1: [
                    { type: 'input_statement', name: 'SUBSTACK' }
                ],
                category: 'control',
                extensions: ['shape_statement']
            });
            applyColors(this, 'control');
        }
    };

    ScratchBlocks.Blocks.python_break = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('BREAK', 'break'),
                category: 'control',
                extensions: ['shape_statement']
            });
            applyColors(this, 'control');
        }
    };

    ScratchBlocks.Blocks.python_continue = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('CONTINUE', 'continue'),
                category: 'control',
                extensions: ['shape_statement']
            });
            applyColors(this, 'control');
        }
    };

    ScratchBlocks.Blocks.python_pass = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('PASS', 'pass'),
                category: 'control',
                extensions: ['shape_statement']
            });
            applyColors(this, 'control');
        }
    };

    // ==========================================
    // 4. CALCULATIONS / OPÉRATEURS
    // ==========================================
    ScratchBlocks.Blocks.python_arithmetic = {
        init: function () {
            this.jsonInit({
                message0: '%1 %2 %3',
                args0: [
                    { type: 'input_value', name: 'A' },
                    {
                        type: 'field_dropdown',
                        name: 'OP',
                        options: getArithmeticOptions()
                    },
                    { type: 'input_value', name: 'B' }
                ],
                category: 'operators',
                extensions: ['output_number']
            });
            applyColors(this, 'operators');
        }
    };

    ScratchBlocks.Blocks.python_round = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('ROUND', 'round %1'),
                args0: [
                    { type: 'input_value', name: 'NUM' }
                ],
                category: 'operators',
                extensions: ['output_number']
            });
            applyColors(this, 'operators');
        }
    };

    ScratchBlocks.Blocks.python_abs = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('ABS', 'abs of %1'),
                args0: [
                    { type: 'input_value', name: 'NUM' }
                ],
                category: 'operators',
                extensions: ['output_number']
            });
            applyColors(this, 'operators');
        }
    };

    ScratchBlocks.Blocks.python_min_max = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('MIN_MAX', '%1 of %2 and %3'),
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'FUNC',
                        options: [['min', 'min'], ['max', 'max']]
                    },
                    { type: 'input_value', name: 'A' },
                    { type: 'input_value', name: 'B' }
                ],
                category: 'operators',
                extensions: ['output_number']
            });
            applyColors(this, 'operators');
        }
    };

    ScratchBlocks.Blocks.python_unary = {
        init: function () {
            this.jsonInit({
                message0: '- %1',
                args0: [
                    { type: 'input_value', name: 'NUM' }
                ],
                category: 'operators',
                extensions: ['output_number']
            });
            applyColors(this, 'operators');
        }
    };

    // ==========================================
    // 5. OUTPUT / SORTIE
    // ==========================================
    ScratchBlocks.Blocks.python_print = {
        init: function () {
            this.partCount_ = 1;
            this.setInputsInline(true);
            this.jsonInit({
                category: 'looks',
                extensions: ['shape_statement']
            });
            applyColors(this, 'looks');

            const input = this.appendValueInput('TEXT');
            const prefix = getPixelBlockMsg('PRINT_PREFIX', 'say');
            if (prefix) {
                input.appendField(prefix, 'PREFIX_LABEL');
            }

            this.updateEndRow_();
        },

        mutationToDom: function () {
            const container = document.createElement('mutation');
            container.setAttribute('parts', String(this.partCount_ || 1));
            return container;
        },

        domToMutation: function (xmlElement) {
            const count = parseInt(xmlElement.getAttribute('parts'), 10) || 1;
            this.setPartCount(count, true /* skipShadow */);
        },

        saveExtraState: function () {
            return {
                parts: this.partCount_ || 1
            };
        },

        loadExtraState: function (state) {
            if (state && state.parts) {
                this.setPartCount(state.parts, true /* skipShadow */);
            }
        },

        setPartCount: function (targetCount, skipShadow = false) {
            targetCount = Math.max(1, targetCount || 1);
            while (this.partCount_ < targetCount) {
                this.addPart_(skipShadow);
            }
            while (this.partCount_ > targetCount) {
                this.removePart_();
            }
        },

        addPart_: function (skipShadow = false) {
            const oldDom = this.mutationToDom();
            const i = this.partCount_;
            this.partCount_++;

            if (this.getInput('END_ROW')) {
                this.removeInput('END_ROW');
            }

            const inputName = 'TEXT' + i;
            const input = this.appendValueInput(inputName);
            input.appendField(',', 'COMMA_' + i);

            if (!skipShadow && this.workspace && !this.isInFlyout && typeof this.workspace.newBlock === 'function') {
                try {
                    const shadow = this.workspace.newBlock('text');
                    shadow.setShadow(true);
                    if (typeof shadow.initSvg === 'function') shadow.initSvg();
                    if (typeof shadow.render === 'function') shadow.render();
                    input.connection.connect(shadow.outputConnection);
                } catch (e) {
                    console.warn('Error attaching shadow:', e);
                }
            }

            this.updateEndRow_();

            if (this.rendered) {
                this.render();
            }
            fireMutationChangeEvent(this, oldDom);
        },

        removePart_: function () {
            if (this.partCount_ <= 1) return;
            const oldDom = this.mutationToDom();
            const i = this.partCount_ - 1;
            this.partCount_--;

            if (this.getInput('END_ROW')) {
                this.removeInput('END_ROW');
            }

            const inputName = 'TEXT' + i;
            if (this.getInput(inputName)) {
                const target = this.getInput(inputName).connection && this.getInput(inputName).connection.targetBlock();
                if (target && target.isShadow()) {
                    try { target.dispose(); } catch (e) {}
                }
                this.removeInput(inputName);
            }

            this.updateEndRow_();

            if (this.rendered) {
                this.render();
            }
            fireMutationChangeEvent(this, oldDom);
        },

        updateEndRow_: function () {
            if (this.getInput('END_ROW')) {
                this.removeInput('END_ROW');
            }

            const endInput = this.appendDummyInput('END_ROW');
            const suffix = getPixelBlockMsg('PRINT_SUFFIX', '');
            if (suffix) {
                endInput.appendField(suffix, 'SUFFIX_LABEL');
            }

            const plusBtn = new ScratchBlocks.FieldImage(
                PLUS_SVG_DATA,
                20,
                20,
                '+',
                () => {
                    if (this.isInFlyout) return;
                    this.addPart_();
                }
            );
            endInput.appendField(plusBtn, 'PLUS_BTN');

            if (this.partCount_ > 1) {
                endInput.appendField(new ScratchBlocks.FieldLabel(' '), 'SEP');
                const minusBtn = new ScratchBlocks.FieldImage(
                    MINUS_SVG_DATA,
                    20,
                    20,
                    '-',
                    () => {
                        if (this.isInFlyout) return;
                        this.removePart_();
                    }
                );
                endInput.appendField(minusBtn, 'MINUS_BTN');
            }
        },

        customContextMenu: function (options) {
            if (this.isInFlyout) return;
            options.push({
                enabled: true,
                text: getPixelBlockMsg('CTX_ADD_PRINT_PART', 'Add value'),
                callback: () => {
                    this.addPart_();
                }
            });
            if (this.partCount_ > 1) {
                options.push({
                    enabled: true,
                    text: getPixelBlockMsg('CTX_REMOVE_PRINT_PART', 'Remove last value'),
                    callback: () => {
                        this.removePart_();
                    }
                });
            }
        }
    };

    ScratchBlocks.Blocks.python_print_multi = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('PRINT_MULTI', 'say %1 , %2'),
                args0: [
                    { type: 'input_value', name: 'A' },
                    { type: 'input_value', name: 'B' }
                ],
                category: 'looks',
                extensions: ['shape_statement']
            });
            applyColors(this, 'looks');
        }
    };

    ScratchBlocks.Blocks.python_print_three = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('PRINT_THREE', 'say %1 , %2 , %3'),
                args0: [
                    { type: 'input_value', name: 'A' },
                    { type: 'input_value', name: 'B' },
                    { type: 'input_value', name: 'C' }
                ],
                category: 'looks',
                extensions: ['shape_statement']
            });
            applyColors(this, 'looks');
        }
    };

    ScratchBlocks.Blocks.python_print_four = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('PRINT_FOUR', 'say %1 , %2 , %3 , %4'),
                args0: [
                    { type: 'input_value', name: 'A' },
                    { type: 'input_value', name: 'B' },
                    { type: 'input_value', name: 'C' },
                    { type: 'input_value', name: 'D' }
                ],
                category: 'looks',
                extensions: ['shape_statement']
            });
            applyColors(this, 'looks');
        }
    };

    ScratchBlocks.Blocks.python_print_five = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('PRINT_FIVE', 'say %1 , %2 , %3 , %4 , %5'),
                args0: [
                    { type: 'input_value', name: 'A' },
                    { type: 'input_value', name: 'B' },
                    { type: 'input_value', name: 'C' },
                    { type: 'input_value', name: 'D' },
                    { type: 'input_value', name: 'E' }
                ],
                category: 'looks',
                extensions: ['shape_statement']
            });
            applyColors(this, 'looks');
        }
    };

    // ==========================================
    // 6. INPUT / ENTRÉE
    // ==========================================
    ScratchBlocks.Blocks.python_input = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('INPUT', 'ask %1 and wait'),
                args0: [
                    { type: 'input_value', name: 'PROMPT' }
                ],
                category: 'sensing',
                extensions: ['output_string']
            });
            applyColors(this, 'sensing');
        }
    };

    ScratchBlocks.Blocks.python_input_int = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('INPUT_INT', 'ask %1 and wait (number)'),
                args0: [
                    { type: 'input_value', name: 'PROMPT' }
                ],
                category: 'sensing',
                extensions: ['output_number']
            });
            applyColors(this, 'sensing');
        }
    };

    ScratchBlocks.Blocks.python_input_float = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('INPUT_FLOAT', 'ask %1 and wait (decimal)'),
                args0: [
                    { type: 'input_value', name: 'PROMPT' }
                ],
                category: 'sensing',
                extensions: ['output_number']
            });
            applyColors(this, 'sensing');
        }
    };

    // ==========================================
    // 7. VALUES / VALEURS
    // ==========================================
    ScratchBlocks.Blocks.python_number = {
        init: function () {
            this.jsonInit({
                message0: '%1',
                args0: [
                    { type: 'field_number', name: 'NUM', value: 0 }
                ],
                category: 'operators',
                extensions: ['output_number']
            });
            applyColors(this, 'operators');
        }
    };

    ScratchBlocks.Blocks.python_string = {
        init: function () {
            this.jsonInit({
                message0: '"%1"',
                args0: [
                    { type: 'field_input', name: 'TEXT', text: '' }
                ],
                category: 'operators',
                extensions: ['output_string']
            });
            applyColors(this, 'operators');
        }
    };

    ScratchBlocks.Blocks.python_boolean = {
        init: function () {
            this.jsonInit({
                message0: '%1',
                args0: [
                    {
                        type: 'field_dropdown',
                        name: 'BOOL',
                        options: getBooleanOptions()
                    }
                ],
                category: 'operators',
                extensions: ['output_boolean']
            });
            applyColors(this, 'operators');
        }
    };

    ScratchBlocks.Blocks.python_none = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('NONE', 'None'),
                category: 'operators',
                extensions: ['output_string']
            });
            applyColors(this, 'operators');
        }
    };

    // ==========================================
    // 8. CONVERSIONS
    // ==========================================
    ScratchBlocks.Blocks.python_cast_int = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('CAST_INT', 'to integer %1'),
                args0: [{ type: 'input_value', name: 'VAL' }],
                category: 'operators',
                extensions: ['output_number']
            });
            applyColors(this, 'operators');
        }
    };

    ScratchBlocks.Blocks.python_cast_float = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('CAST_FLOAT', 'to decimal %1'),
                args0: [{ type: 'input_value', name: 'VAL' }],
                category: 'operators',
                extensions: ['output_number']
            });
            applyColors(this, 'operators');
        }
    };

    ScratchBlocks.Blocks.python_cast_str = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('CAST_STR', 'to text %1'),
                args0: [{ type: 'input_value', name: 'VAL' }],
                category: 'operators',
                extensions: ['output_string']
            });
            applyColors(this, 'operators');
        }
    };

    ScratchBlocks.Blocks.python_cast_bool = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('CAST_BOOL', 'to boolean %1'),
                args0: [{ type: 'input_value', name: 'VAL' }],
                category: 'operators',
                extensions: ['output_boolean']
            });
            applyColors(this, 'operators');
        }
    };

    ScratchBlocks.Blocks.python_type = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('TYPE', 'type of %1'),
                args0: [{ type: 'input_value', name: 'VAL' }],
                category: 'operators',
                extensions: ['output_string']
            });
            applyColors(this, 'operators');
        }
    };

    // ==========================================
    // 9. LISTS / LISTES
    // ==========================================
    ScratchBlocks.Blocks.python_list_empty = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('LIST_EMPTY', 'empty list'),
                category: 'lists',
                extensions: ['output_string']
            });
            applyColors(this, 'lists');
        }
    };

    ScratchBlocks.Blocks.python_list_create_3 = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('LIST_CREATE_3', 'list with %1 , %2 , %3'),
                args0: [
                    { type: 'input_value', name: 'A' },
                    { type: 'input_value', name: 'B' },
                    { type: 'input_value', name: 'C' }
                ],
                category: 'lists',
                extensions: ['output_string']
            });
            applyColors(this, 'lists');
        }
    };

    ScratchBlocks.Blocks.python_list_append = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('LIST_APPEND', 'add %2 to %1'),
                args0: [
                    { type: 'input_value', name: 'LIST' },
                    { type: 'input_value', name: 'ITEM' }
                ],
                category: 'lists',
                extensions: ['shape_statement']
            });
            applyColors(this, 'lists');
        }
    };

    ScratchBlocks.Blocks.python_list_pop = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('LIST_POP', 'delete item %2 of %1'),
                args0: [
                    { type: 'input_value', name: 'LIST' },
                    { type: 'input_value', name: 'INDEX' }
                ],
                category: 'lists',
                extensions: ['output_string']
            });
            applyColors(this, 'lists');
        }
    };

    ScratchBlocks.Blocks.python_list_remove = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('LIST_REMOVE', 'delete %2 of %1'),
                args0: [
                    { type: 'input_value', name: 'LIST' },
                    { type: 'input_value', name: 'ITEM' }
                ],
                category: 'lists',
                extensions: ['shape_statement']
            });
            applyColors(this, 'lists');
        }
    };

    ScratchBlocks.Blocks.python_list_insert = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('LIST_INSERT', 'insert %3 at %2 of %1'),
                args0: [
                    { type: 'input_value', name: 'LIST' },
                    { type: 'input_value', name: 'INDEX' },
                    { type: 'input_value', name: 'ITEM' }
                ],
                category: 'lists',
                extensions: ['shape_statement']
            });
            applyColors(this, 'lists');
        }
    };

    ScratchBlocks.Blocks.python_list_get = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('LIST_GET', 'item %2 of %1'),
                args0: [
                    { type: 'input_value', name: 'LIST' },
                    { type: 'input_value', name: 'INDEX' }
                ],
                category: 'lists',
                extensions: ['output_string']
            });
            applyColors(this, 'lists');
        }
    };

    ScratchBlocks.Blocks.python_list_set = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('LIST_SET', 'replace item %2 of %1 with %3'),
                args0: [
                    { type: 'input_value', name: 'LIST' },
                    { type: 'input_value', name: 'INDEX' },
                    { type: 'input_value', name: 'VALUE' }
                ],
                category: 'lists',
                extensions: ['shape_statement']
            });
            applyColors(this, 'lists');
        }
    };

    ScratchBlocks.Blocks.python_list_len = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('LIST_LEN', 'length of %1'),
                args0: [
                    { type: 'input_value', name: 'LIST' }
                ],
                category: 'lists',
                extensions: ['output_number']
            });
            applyColors(this, 'lists');
        }
    };

    ScratchBlocks.Blocks.python_range = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('RANGE', 'numbers 0 to %1'),
                args0: [
                    { type: 'input_value', name: 'STOP' }
                ],
                category: 'lists',
                extensions: ['output_string']
            });
            applyColors(this, 'lists');
        }
    };

    ScratchBlocks.Blocks.python_range_start_stop = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('RANGE_START_STOP', 'numbers %1 to %2'),
                args0: [
                    { type: 'input_value', name: 'START' },
                    { type: 'input_value', name: 'STOP' }
                ],
                category: 'lists',
                extensions: ['output_string']
            });
            applyColors(this, 'lists');
        }
    };

    // ==========================================
    // 10. DICTIONARIES / DICTIONNAIRES
    // ==========================================
    ScratchBlocks.Blocks.python_dict_empty = {
        init: function () {
            this.jsonInit({
                message0: '{ }',
                category: 'dictionaries',
                extensions: ['output_string']
            });
            applyColors(this, 'dictionaries');
        }
    };

    ScratchBlocks.Blocks.python_dict_create = {
        init: function () {
            this.jsonInit({
                message0: '{ %1 : %2 , %3 : %4 }',
                args0: [
                    { type: 'input_value', name: 'KEY1' },
                    { type: 'input_value', name: 'VAL1' },
                    { type: 'input_value', name: 'KEY2' },
                    { type: 'input_value', name: 'VAL2' }
                ],
                category: 'dictionaries',
                extensions: ['output_string']
            });
            applyColors(this, 'dictionaries');
        }
    };

    ScratchBlocks.Blocks.python_dict_get = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('LIST_GET', 'item %2 of %1'),
                args0: [
                    { type: 'input_value', name: 'DICT' },
                    { type: 'input_value', name: 'KEY' }
                ],
                category: 'dictionaries',
                extensions: ['output_string']
            });
            applyColors(this, 'dictionaries');
        }
    };

    ScratchBlocks.Blocks.python_dict_set = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('LIST_SET', 'replace item %2 of %1 with %3'),
                args0: [
                    { type: 'input_value', name: 'DICT' },
                    { type: 'input_value', name: 'KEY' },
                    { type: 'input_value', name: 'VAL' }
                ],
                category: 'dictionaries',
                extensions: ['shape_statement']
            });
            applyColors(this, 'dictionaries');
        }
    };

    ScratchBlocks.Blocks.python_dict_keys = {
        init: function () {
            this.jsonInit({
                message0: '%1 . keys ()',
                args0: [{ type: 'input_value', name: 'DICT' }],
                category: 'dictionaries',
                extensions: ['output_string']
            });
            applyColors(this, 'dictionaries');
        }
    };

    ScratchBlocks.Blocks.python_dict_values = {
        init: function () {
            this.jsonInit({
                message0: '%1 . values ()',
                args0: [{ type: 'input_value', name: 'DICT' }],
                category: 'dictionaries',
                extensions: ['output_string']
            });
            applyColors(this, 'dictionaries');
        }
    };

    // ==========================================
    // 11. FUNCTIONS / FONCTIONS
    // ==========================================
    ScratchBlocks.Blocks.python_def = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('DEF', 'def %1 () :'),
                message1: '%1',
                args0: [
                    { type: 'field_input', name: 'NAME', text: 'my_function' }
                ],
                args1: [
                    { type: 'input_statement', name: 'SUBSTACK' }
                ],
                category: 'functions',
                extensions: ['shape_statement']
            });
            applyColors(this, 'functions');
        }
    };

    ScratchBlocks.Blocks.python_def_one_arg = {
        init: function () {
            this.jsonInit({
                message0: 'def %1 ( %2 ) :',
                message1: '%1',
                args0: [
                    { type: 'field_input', name: 'NAME', text: 'my_function' },
                    { type: 'field_input', name: 'ARG1', text: 'arg' }
                ],
                args1: [
                    { type: 'input_statement', name: 'SUBSTACK' }
                ],
                category: 'functions',
                extensions: ['shape_statement']
            });
            applyColors(this, 'functions');
        }
    };

    ScratchBlocks.Blocks.python_def_two_args = {
        init: function () {
            this.jsonInit({
                message0: 'def %1 ( %2 , %3 ) :',
                message1: '%1',
                args0: [
                    { type: 'field_input', name: 'NAME', text: 'my_function' },
                    { type: 'field_input', name: 'ARG1', text: 'a' },
                    { type: 'field_input', name: 'ARG2', text: 'b' }
                ],
                args1: [
                    { type: 'input_statement', name: 'SUBSTACK' }
                ],
                category: 'functions',
                extensions: ['shape_statement']
            });
            applyColors(this, 'functions');
        }
    };

    ScratchBlocks.Blocks.python_return = {
        init: function () {
            this.jsonInit({
                message0: getPixelBlockMsg('RETURN', 'return %1'),
                args0: [
                    { type: 'input_value', name: 'VAL' }
                ],
                category: 'functions',
                extensions: ['shape_statement']
            });
            applyColors(this, 'functions');
        }
    };

    ScratchBlocks.Blocks.python_call_stmt = {
        init: function () {
            this.jsonInit({
                message0: '%1 ()',
                args0: [
                    { type: 'field_input', name: 'NAME', text: 'my_function' }
                ],
                category: 'functions',
                extensions: ['shape_statement']
            });
            applyColors(this, 'functions');
        }
    };

    ScratchBlocks.Blocks.python_call_stmt_arg = {
        init: function () {
            this.jsonInit({
                message0: '%1 ( %2 )',
                args0: [
                    { type: 'field_input', name: 'NAME', text: 'my_function' },
                    { type: 'input_value', name: 'ARG' }
                ],
                category: 'functions',
                extensions: ['shape_statement']
            });
            applyColors(this, 'functions');
        }
    };

    ScratchBlocks.Blocks.python_call_expr = {
        init: function () {
            this.jsonInit({
                message0: '%1 ()',
                args0: [
                    { type: 'field_input', name: 'NAME', text: 'my_function' }
                ],
                category: 'functions',
                extensions: ['output_string']
            });
            applyColors(this, 'functions');
        }
    };

    ScratchBlocks.Blocks.python_call_expr_arg = {
        init: function () {
            this.jsonInit({
                message0: '%1 ( %2 )',
                args0: [
                    { type: 'field_input', name: 'NAME', text: 'my_function' },
                    { type: 'input_value', name: 'ARG' }
                ],
                category: 'functions',
                extensions: ['output_string']
            });
            applyColors(this, 'functions');
        }
    };

    // ==========================================
    // 12. TURTLE / GRAPHIQUES
    // ==========================================
    ScratchBlocks.Blocks.python_turtle_forward = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . forward ( %1 )',
                args0: [{ type: 'input_value', name: 'DIST' }],
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_backward = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . backward ( %1 )',
                args0: [{ type: 'input_value', name: 'DIST' }],
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_right = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . right ( %1 )',
                args0: [{ type: 'input_value', name: 'ANGLE' }],
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_left = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . left ( %1 )',
                args0: [{ type: 'input_value', name: 'ANGLE' }],
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_goto = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . goto ( %1 , %2 )',
                args0: [
                    { type: 'input_value', name: 'X' },
                    { type: 'input_value', name: 'Y' }
                ],
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_setheading = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . setheading ( %1 )',
                args0: [{ type: 'input_value', name: 'ANGLE' }],
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_penup = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . penup ()',
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_pendown = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . pendown ()',
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_color = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . pencolor ( %1 )',
                args0: [{ type: 'input_value', name: 'COLOR' }],
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_pensize = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . pensize ( %1 )',
                args0: [{ type: 'input_value', name: 'SIZE' }],
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_circle = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . circle ( %1 )',
                args0: [{ type: 'input_value', name: 'RADIUS' }],
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_speed = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . speed ( %1 )',
                args0: [{ type: 'input_value', name: 'SPEED' }],
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_clear = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . clear ()',
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_reset = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . reset ()',
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    ScratchBlocks.Blocks.python_turtle_mainloop = {
        init: function () {
            this.jsonInit({
                message0: 'turtle . mainloop ()',
                category: 'turtle',
                extensions: ['shape_statement']
            });
            applyColors(this, 'turtle');
        }
    };

    // ==========================================
    // 13. CLASSES & MODULES
    // ==========================================
    ScratchBlocks.Blocks.python_import = {
        init: function () {
            this.jsonInit({
                message0: 'import %1',
                args0: [{ type: 'field_input', name: 'MODULE', text: 'math' }],
                category: 'classes',
                extensions: ['shape_statement']
            });
            applyColors(this, 'classes');
        }
    };

    ScratchBlocks.Blocks.python_from_import = {
        init: function () {
            this.jsonInit({
                message0: 'from %1 import %2',
                args0: [
                    { type: 'field_input', name: 'MODULE', text: 'random' },
                    { type: 'field_input', name: 'ITEM', text: 'randint' }
                ],
                category: 'classes',
                extensions: ['shape_statement']
            });
            applyColors(this, 'classes');
        }
    };

    ScratchBlocks.Blocks.python_import_as = {
        init: function () {
            this.jsonInit({
                message0: 'import %1 as %2',
                args0: [
                    { type: 'field_input', name: 'MODULE', text: 'numpy' },
                    { type: 'field_input', name: 'ALIAS', text: 'np' }
                ],
                category: 'classes',
                extensions: ['shape_statement']
            });
            applyColors(this, 'classes');
        }
    };

    ScratchBlocks.Blocks.python_class = {
        init: function () {
            this.jsonInit({
                message0: 'class %1 :',
                message1: '%1',
                args0: [{ type: 'field_input', name: 'NAME', text: 'MyClass' }],
                args1: [{ type: 'input_statement', name: 'SUBSTACK' }],
                category: 'classes',
                extensions: ['shape_statement']
            });
            applyColors(this, 'classes');
        }
    };

    ScratchBlocks.Blocks.python_comment = {
        init: function () {
            this.jsonInit({
                message0: '# %1',
                args0: [{ type: 'field_input', name: 'COMMENT', text: 'Write note here...' }],
                category: 'classes',
                extensions: ['shape_statement']
            });
            applyColors(this, 'classes');
        }
    };

    ScratchBlocks.Blocks.python_raw = {
        init: function () {
            this.jsonInit({
                message0: 'python: %1',
                args0: [{ type: 'field_input', name: 'CODE', text: 'x = 42' }],
                category: 'classes',
                extensions: ['shape_statement']
            });
            applyColors(this, 'classes');
        }
    };

    ScratchBlocks.Blocks.python_raw_expr = {
        init: function () {
            this.jsonInit({
                message0: '%1',
                args0: [{ type: 'field_input', name: 'CODE', text: 'None' }],
                category: 'classes',
                extensions: ['output_string']
            });
            applyColors(this, 'classes');
        }
    };
}
